import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import Modal from "@/components/Modal";
import { useDebounce } from "@/hooks/useDebounce";
import { PaginationData, Sekolah } from "@/types";
import { GoPlus } from "react-icons/go";
import { MdSync } from "react-icons/md";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import FormSync from "./FormSync";
import { Pagination } from "./Pagination";
import Form from "./Form";
import FormImport from "./FormImport";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  paginationData: PaginationData<Sekolah>;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  paginationData,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const [showImport, setShowImport] = useState(false);
  const [showInsert, setShowInsert] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [showSyncAll, setShowSyncAll] = useState(false);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || ""
  );
  const debouncedQuery = useDebounce(searchQuery, 1000);
  const navigate = useNavigate();
  const location = useLocation();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (debouncedQuery) {
      if (params.get("search") !== debouncedQuery) {
        params.delete("page");
      }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search]);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-4">
        <Input
          autoFocus
          placeholder="Filter by nama sekolah, npsn sekolah..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="max-w-sm"
        />
        <div className="flex gap-x-3">
          <Button
            className="flex items-center gap-x-3"
            onClick={() => setShowSync(true)}
          >
            Sinkronisasi Per Wilayah
            <MdSync />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncAll(true)}
          >
            Sinkronisasi Semua Sekolah
            <MdSync />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowImport(true)}
          >
            Import
            <GoPlus />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowInsert(true)}
          >
            Insert
            <GoPlus />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="border rounded-xl">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal
        show={showSync}
        onClose={() => setShowSync(false)}
        closeable={false}
      >
        <FormSync closeModal={() => setShowSync(false)} />
      </Modal>

      <Modal
        show={showSyncAll}
        onClose={() => setShowSyncAll(false)}
        closeable={false}
      >
        <FormSync closeModal={() => setShowSyncAll(false)} isSyncAll={true} />
      </Modal>

      <Modal show={showInsert} onClose={() => setShowInsert(false)}>
        <Form method="POST" closeModal={() => setShowInsert(false)} />
      </Modal>

      <Modal
        show={showImport}
        onClose={() => setShowImport(false)}
        closeable={false}
      >
        <FormImport closeModal={() => setShowImport(false)} />
      </Modal>
    </div>
  );
}
